import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { 
  Shield, Key, Clock, CheckCircle, XCircle, AlertTriangle, 
  Copy, RefreshCw, FileText, Activity, Lock, Unlock, 
  Calendar, User, DollarSign, Package, Download, Calculator,
  Building, Users, Mail, Phone, MapPin, Hash, Briefcase,
  AlertCircle, ChevronRight, FileCheck, Truck, CheckSquare,
  BarChart3, TrendingUp, ShieldAlert, Play, Eye, FileDown,
  PieChart, Target, AlertOctagon, Percent, Wallet, Zap,
  TrendingDown, LineChart, Send, Flame
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export default function PSCLAdmin() {
  const [license, setLicense] = useState(null);
  const [supportStatus, setSupportStatus] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [quotations, setQuotations] = useState([]);
  const [commercialMetrics, setCommercialMetrics] = useState(null);
  const [integrityStatus, setIntegrityStatus] = useState(null);
  const [enforcementLogs, setEnforcementLogs] = useState([]);
  
  // Phase 2: New state variables
  const [revenueForecast, setRevenueForecast] = useState(null);
  const [riskKpis, setRiskKpis] = useState(null);
  const [downloadStats, setDownloadStats] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  
  // Sales Enablement state
  const [commercialHealth, setCommercialHealth] = useState(null);
  const [pricingSimulation, setPricingSimulation] = useState(null);
  const [salesScripts, setSalesScripts] = useState(null);
  
  // Phase 4: Revenue Execution state
  const [dealMetrics, setDealMetrics] = useState(null);
  const [hotLeads, setHotLeads] = useState([]);
  const [revenueReadiness, setRevenueReadiness] = useState(null);
  const [marketSimulation, setMarketSimulation] = useState(null);
  const [expiredLicenses, setExpiredLicenses] = useState([]);
  const [reactivationOffers, setReactivationOffers] = useState([]);
  const [showReactivationOfferDialog, setShowReactivationOfferDialog] = useState(false);
  const [selectedLicenseForOffer, setSelectedLicenseForOffer] = useState(null);
  const [proposalForm, setProposalForm] = useState({
    client_name: '',
    client_legal_entity: '',
    license_tier: 'professional',
    contact_person: '',
    contact_email: ''
  });
  const [simulatorInputs, setSimulatorInputs] = useState({
    license_price: 55000,
    support_rate: 0.20,
    year1_renewal: 0.75,
    year2_renewal: 0.70,
    year3_renewal: 0.65,
    new_licenses_year1: 10,
    new_licenses_year2: 15,
    new_licenses_year3: 20
  });
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Dialog states
  const [showQuotationDialog, setShowQuotationDialog] = useState(false);
  const [showReactivationDialog, setShowReactivationDialog] = useState(false);
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  
  // Form states
  const [quotationForm, setQuotationForm] = useState({
    customer_legal_entity: '',
    customer_contact_name: '',
    customer_contact_email: '',
    customer_address: '',
    employee_count: 100,
    include_migration: false,
    customization_hours: 0,
    include_support: false
  });
  const [quotationPreview, setQuotationPreview] = useState(null);
  const [reactivationCalc, setReactivationCalc] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: 0, reference: '' });
  const [transferForm, setTransferForm] = useState({ package_hash: '', version: '1.0.0' });
  
  const [actionLoading, setActionLoading] = useState(false);

  const user = JSON.parse(localStorage.getItem('hrms_user') || '{}');
  const isOwner = user.role === 'admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [licenseRes, supportRes, pricingRes, quotationsRes] = await Promise.all([
        axios.get(`${API}/pscl/license`),
        axios.get(`${API}/pscl/support-status`),
        axios.get(`${API}/pscl/pricing`),
        isOwner ? axios.get(`${API}/pscl/quotations`) : Promise.resolve({ data: [] })
      ]);
      
      setLicense(licenseRes.data.exists ? licenseRes.data.license : null);
      setSupportStatus(supportRes.data);
      setPricing(pricingRes.data);
      setQuotations(quotationsRes.data || []);
      
      // Fetch commercial metrics if owner
      if (isOwner) {
        try {
          const metricsRes = await axios.get(`${API}/pscl/commercial-metrics`);
          setCommercialMetrics(metricsRes.data);
        } catch (e) {
          console.log('Metrics not available');
        }
      }
    } catch (error) {
      console.error('Failed to fetch PSCL data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchIntegrityCheck = async () => {
    setActionLoading(true);
    try {
      const res = await axios.get(`${API}/pscl/integrity-check`);
      setIntegrityStatus(res.data);
      if (res.data.integrity_verified) {
        toast.success('Integrity check passed - no tampering detected');
      } else {
        toast.error('Integrity issues detected! Review the report.');
      }
    } catch (error) {
      toast.error('Failed to run integrity check');
    } finally {
      setActionLoading(false);
    }
  };

  const fetchEnforcementLogs = async () => {
    try {
      const res = await axios.get(`${API}/pscl/enforcement-logs?limit=20`);
      setEnforcementLogs(res.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch enforcement logs');
    }
  };

  // Phase 2: New fetch functions
  const fetchRevenueForecast = async () => {
    try {
      const res = await axios.get(`${API}/pscl/revenue-forecast`);
      setRevenueForecast(res.data);
    } catch (error) {
      console.error('Failed to fetch revenue forecast');
    }
  };

  const fetchRiskKpis = async () => {
    try {
      const res = await axios.get(`${API}/pscl/commercial-risk-kpis`);
      setRiskKpis(res.data);
    } catch (error) {
      console.error('Failed to fetch risk KPIs');
    }
  };

  const fetchDownloadStats = async () => {
    try {
      const res = await axios.get(`${API}/pscl/download/stats`);
      setDownloadStats(res.data);
    } catch (error) {
      console.error('Failed to fetch download stats');
    }
  };

  const runEnforcementValidation = async () => {
    setActionLoading(true);
    try {
      const res = await axios.get(`${API}/pscl/enforcement-validation`);
      setValidationResult(res.data);
      if (res.data.summary?.enforcement_status === 'VERIFIED') {
        toast.success('All enforcement checks passed!');
      } else {
        toast.warning('Some enforcement checks failed - review required');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run validation');
    } finally {
      setActionLoading(false);
    }
  };

  const downloadValidationPDF = async () => {
    setActionLoading(true);
    try {
      const response = await axios.get(`${API}/pscl/enforcement-validation/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `enforcement_validation_report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Validation report downloaded');
    } catch (error) {
      toast.error('Failed to download validation report');
    } finally {
      setActionLoading(false);
    }
  };

  const exportAuditLog = async (logType) => {
    setActionLoading(true);
    try {
      const response = await axios.get(`${API}/pscl/audit-log/export?log_type=${logType}&format=json`);
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${logType}_audit_log.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${logType} audit log exported`);
    } catch (error) {
      toast.error('Failed to export audit log');
    } finally {
      setActionLoading(false);
    }
  };

  // Sales Enablement functions
  const fetchCommercialHealth = async () => {
    try {
      const res = await axios.get(`${API}/pscl/commercial-health`);
      setCommercialHealth(res.data);
    } catch (error) {
      console.error('Failed to fetch commercial health');
    }
  };

  const fetchSalesScripts = async () => {
    try {
      const res = await axios.get(`${API}/pscl/sales-scripts`);
      setSalesScripts(res.data);
    } catch (error) {
      console.error('Failed to fetch sales scripts');
    }
  };

  const runPricingSimulation = async () => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/pscl/pricing-simulator`, simulatorInputs);
      setPricingSimulation(res.data);
      toast.success('Simulation completed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run simulation');
    } finally {
      setActionLoading(false);
    }
  };

  const generateProposal = async () => {
    if (!proposalForm.client_name || !proposalForm.client_legal_entity) {
      toast.error('Client name and legal entity are required');
      return;
    }
    setActionLoading(true);
    try {
      const response = await axios.post(`${API}/pscl/generate-proposal`, proposalForm, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `proposal_${proposalForm.client_name.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Proposal generated and downloaded');
      setProposalForm({ client_name: '', client_legal_entity: '', license_tier: 'professional', contact_person: '', contact_email: '' });
    } catch (error) {
      toast.error('Failed to generate proposal');
    } finally {
      setActionLoading(false);
    }
  };

  const downloadComparisonSheet = async () => {
    setActionLoading(true);
    try {
      const response = await axios.post(`${API}/pscl/generate-comparison`, {
        license_price: simulatorInputs.license_price,
        employee_count: 100,
        comparison_years: 3
      }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'pscl_comparison_sheet.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Comparison sheet downloaded');
    } catch (error) {
      toast.error('Failed to generate comparison sheet');
    } finally {
      setActionLoading(false);
    }
  };

  const downloadSalesPlaybook = async () => {
    setActionLoading(true);
    try {
      const response = await axios.get(`${API}/pscl/sales-scripts/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'sales_playbook.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Sales playbook downloaded');
    } catch (error) {
      toast.error('Failed to download playbook');
    } finally {
      setActionLoading(false);
    }
  };

  // Phase 4: Revenue Execution functions
  const fetchDealMetrics = async () => {
    try {
      const res = await axios.get(`${API}/pscl/deal-metrics`);
      setDealMetrics(res.data);
    } catch (error) {
      console.error('Failed to fetch deal metrics');
    }
  };

  const fetchHotLeads = async () => {
    try {
      const res = await axios.get(`${API}/pscl/hot-leads`);
      setHotLeads(res.data.hot_leads || []);
    } catch (error) {
      console.error('Failed to fetch hot leads');
    }
  };

  const fetchRevenueReadiness = async () => {
    try {
      const res = await axios.get(`${API}/pscl/revenue-readiness`);
      setRevenueReadiness(res.data);
    } catch (error) {
      console.error('Failed to fetch revenue readiness');
    }
  };

  const fetchMarketSimulation = async () => {
    setActionLoading(true);
    try {
      const res = await axios.get(`${API}/pscl/market-simulation/default`);
      setMarketSimulation(res.data);
    } catch (error) {
      toast.error('Failed to load market simulation');
    } finally {
      setActionLoading(false);
    }
  };

  const fetchExpiredLicenses = async () => {
    try {
      const res = await axios.get(`${API}/pscl/expired-licenses`);
      setExpiredLicenses(res.data.expired_licenses || []);
    } catch (error) {
      console.error('Failed to fetch expired licenses');
    }
  };

  const fetchReactivationOffers = async () => {
    try {
      const res = await axios.get(`${API}/pscl/reactivation-offers`);
      setReactivationOffers(res.data.offers || []);
    } catch (error) {
      console.error('Failed to fetch reactivation offers');
    }
  };

  const generateReactivationOffer = async (license) => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/pscl/reactivation-offer`, {
        license_id: license.license_id,
        discount_percentage: 0
      });
      toast.success('Reactivation offer generated with 30-day expiry');
      setShowReactivationOfferDialog(false);
      fetchReactivationOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate offer');
    } finally {
      setActionLoading(false);
    }
  };

  const sendReactivationOffer = async (offerId) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/pscl/reactivation-offer/${offerId}/send`);
      toast.success('Reactivation offer sent');
      fetchReactivationOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send offer');
    } finally {
      setActionLoading(false);
    }
  };

  const runSchedulerNow = async () => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/pscl/run-scheduler-now`);
      toast.success('Scheduler tasks executed successfully');
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run scheduler');
    } finally {
      setActionLoading(false);
    }
  };

  const recalculateHashes = async () => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/pscl/recalculate-integrity-hashes`);
      toast.success(`Integrity hashes updated for ${res.data.records_updated?.length || 0} records`);
      fetchIntegrityCheck();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to recalculate hashes');
    } finally {
      setActionLoading(false);
    }
  };

  const calculateQuotation = async () => {
    try {
      const res = await axios.get(`${API}/pscl/quotation/calculate`, {
        params: {
          employee_count: quotationForm.employee_count,
          include_migration: quotationForm.include_migration,
          customization_hours: quotationForm.customization_hours,
          include_support: quotationForm.include_support
        }
      });
      setQuotationPreview(res.data);
    } catch (error) {
      toast.error('Failed to calculate quotation');
    }
  };

  const createQuotation = async () => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/pscl/quotation`, quotationForm);
      toast.success('Quotation created successfully');
      setShowQuotationDialog(false);
      fetchData();
      
      // Download PDF
      window.open(`${API}/pscl/quotation/${res.data.quote_id}/pdf`, '_blank');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create quotation');
    } finally {
      setActionLoading(false);
    }
  };

  const calculateReactivation = async () => {
    setActionLoading(true);
    try {
      const res = await axios.get(`${API}/pscl/reactivation-calculate`);
      setReactivationCalc(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to calculate reactivation');
    } finally {
      setActionLoading(false);
    }
  };

  const activateSupport = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/pscl/support/activate`, {
        payment_amount: paymentForm.amount,
        payment_reference: paymentForm.reference
      });
      toast.success('Support activated successfully');
      setShowActivateDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate support');
    } finally {
      setActionLoading(false);
    }
  };

  const processReactivation = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/pscl/reactivation-process`, {
        payment_amount: reactivationCalc?.total_to_pay || 0,
        payment_reference: paymentForm.reference
      });
      toast.success('Support reactivated successfully');
      setShowReactivationDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate support');
    } finally {
      setActionLoading(false);
    }
  };

  const recordTransfer = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/pscl/source-code-transfer`, transferForm);
      toast.success('Source code transfer recorded');
      setShowTransferDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record transfer');
    } finally {
      setActionLoading(false);
    }
  };

  const confirmTransfer = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/pscl/source-code-confirm`);
      toast.success('Source code receipt confirmed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm receipt');
    } finally {
      setActionLoading(false);
    }
  };

  const downloadQuotationPDF = async (quoteId) => {
    window.open(`${API}/pscl/quotation/${quoteId}/pdf`, '_blank');
  };

  useEffect(() => {
    if (showQuotationDialog && quotationForm.employee_count > 0) {
      calculateQuotation();
    }
  }, [quotationForm.employee_count, quotationForm.include_migration, 
      quotationForm.customization_hours, quotationForm.include_support]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  const getSupportStatusBadge = () => {
    if (!supportStatus) return null;
    const status = supportStatus.support_status;
    
    const badges = {
      active: <Badge className="bg-green-600">Active</Badge>,
      inactive: <Badge className="bg-gray-600">Inactive (Not Purchased)</Badge>,
      expired: <Badge className="bg-red-600">Expired</Badge>,
      reactivation_required: <Badge className="bg-orange-600">Reactivation Required</Badge>
    };
    
    return badges[status] || <Badge className="bg-gray-600">{status}</Badge>;
  };

  return (
    <div className="p-6 space-y-6" data-testid="pscl-admin">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="w-7 h-7 text-blue-400" />
            PSCL License Administration
          </h1>
          <p className="text-gray-400 mt-1">Perpetual Source Code License Management</p>
        </div>
        {isOwner && (
          <Button onClick={() => setShowQuotationDialog(true)} className="bg-blue-600 hover:bg-blue-700">
            <FileText className="w-4 h-4 mr-2" />
            Create Quotation
          </Button>
        )}
      </div>

      {/* Key Message Banner */}
      <Card className="bg-gradient-to-r from-blue-900/50 to-purple-900/50 border-blue-700">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-blue-400" />
            <div>
              <p className="text-white font-semibold">License NEVER Expires • System ALWAYS Runs</p>
              <p className="text-gray-300 text-sm">Support is optional. Core system operates independently of support status.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-800 flex-wrap">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="support">Support Status</TabsTrigger>
          <TabsTrigger value="transfer">Source Code Transfer</TabsTrigger>
          {isOwner && <TabsTrigger value="validation">Validation</TabsTrigger>}
          {isOwner && <TabsTrigger value="sales">Sales Enablement</TabsTrigger>}
          {isOwner && <TabsTrigger value="revenue">Revenue Execution</TabsTrigger>}
          {isOwner && <TabsTrigger value="metrics">Commercial Metrics</TabsTrigger>}
          {isOwner && <TabsTrigger value="quotations">Quotations</TabsTrigger>}
          <TabsTrigger value="pricing">Pricing Reference</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* License Info */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Key className="w-5 h-5 text-blue-400" />
                  License Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {license ? (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-400">License ID</span>
                      <span className="text-white font-mono text-sm">{license.license_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Deployment ID</span>
                      <span className="text-white font-mono text-sm">{license.deployment_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">License Type</span>
                      <Badge className="bg-purple-600">{license.license_type}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Max Employees</span>
                      <span className="text-white">{license.max_employees}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">License Fee</span>
                      <span className="text-green-400 font-semibold">RM {license.license_fee?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Status</span>
                      <Badge className="bg-green-600">Perpetual (Never Expires)</Badge>
                    </div>
                  </>
                ) : (
                  <p className="text-gray-400">No PSCL license installed</p>
                )}
              </CardContent>
            </Card>

            {/* Customer Info */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Building className="w-5 h-5 text-blue-400" />
                  Customer Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {license ? (
                  <>
                    <div className="flex justify-between items-start">
                      <span className="text-gray-400">Legal Entity</span>
                      <span className="text-white text-right">{license.customer_legal_entity}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Contact</span>
                      <span className="text-white">{license.customer_contact_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Email</span>
                      <span className="text-blue-400">{license.customer_contact_email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Domain</span>
                      <span className="text-white">{license.primary_domain}</span>
                    </div>
                  </>
                ) : (
                  <p className="text-gray-400">No license data</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Support Summary */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Activity className="w-5 h-5 text-blue-400" />
                  Support Status Summary
                </CardTitle>
                {getSupportStatusBadge()}
              </div>
            </CardHeader>
            <CardContent>
              {supportStatus && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-900 p-4 rounded-lg text-center">
                    <p className="text-gray-400 text-sm">Updates Access</p>
                    {supportStatus.can_access_updates ? (
                      <CheckCircle className="w-8 h-8 text-green-400 mx-auto mt-2" />
                    ) : (
                      <XCircle className="w-8 h-8 text-red-400 mx-auto mt-2" />
                    )}
                  </div>
                  <div className="bg-gray-900 p-4 rounded-lg text-center">
                    <p className="text-gray-400 text-sm">Security Patches</p>
                    {supportStatus.can_access_patches ? (
                      <CheckCircle className="w-8 h-8 text-green-400 mx-auto mt-2" />
                    ) : (
                      <XCircle className="w-8 h-8 text-red-400 mx-auto mt-2" />
                    )}
                  </div>
                  <div className="bg-gray-900 p-4 rounded-lg text-center">
                    <p className="text-gray-400 text-sm">Vendor Support</p>
                    {supportStatus.can_use_vendor_admin ? (
                      <CheckCircle className="w-8 h-8 text-green-400 mx-auto mt-2" />
                    ) : (
                      <XCircle className="w-8 h-8 text-red-400 mx-auto mt-2" />
                    )}
                  </div>
                  <div className="bg-gray-900 p-4 rounded-lg text-center">
                    <p className="text-gray-400 text-sm">Days Remaining</p>
                    <p className={`text-2xl font-bold mt-2 ${
                      supportStatus.days_remaining > 30 ? 'text-green-400' : 
                      supportStatus.days_remaining > 0 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                      {supportStatus.days_remaining ?? 'N/A'}
                    </p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {isOwner && supportStatus && (
                <div className="flex gap-3 mt-4">
                  {supportStatus.support_status === 'inactive' && (
                    <Button onClick={() => setShowActivateDialog(true)} className="bg-green-600 hover:bg-green-700">
                      <Unlock className="w-4 h-4 mr-2" />
                      Activate Support
                    </Button>
                  )}
                  {supportStatus.requires_reactivation && (
                    <Button onClick={() => { calculateReactivation(); setShowReactivationDialog(true); }} className="bg-orange-600 hover:bg-orange-700">
                      <Calculator className="w-4 h-4 mr-2" />
                      Calculate Reactivation
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Support Status Tab */}
        <TabsContent value="support" className="space-y-4">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Support Subscription Details</CardTitle>
              <CardDescription>Support is OPTIONAL from Day 1. Your license is perpetual regardless of support status.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {supportStatus?.subscription ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-gray-400 text-sm">Period Start</p>
                      <p className="text-white">{supportStatus.subscription.period_start?.slice(0, 10) || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Period End</p>
                      <p className="text-white">{supportStatus.subscription.period_end?.slice(0, 10) || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Annual Fee</p>
                      <p className="text-green-400 font-semibold">RM {supportStatus.annual_support_fee?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Status</p>
                      {getSupportStatusBadge()}
                    </div>
                  </div>

                  {supportStatus.is_expired && (
                    <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 mt-4">
                      <div className="flex items-center gap-2 text-red-400 mb-2">
                        <AlertTriangle className="w-5 h-5" />
                        <span className="font-semibold">Support Expired</span>
                      </div>
                      <p className="text-gray-300 text-sm">
                        Your support subscription has expired. Your system continues to operate normally, 
                        but you will not receive updates, patches, or vendor support until reactivated.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-gray-900 border border-gray-700 rounded-lg p-4">
                  <p className="text-gray-400">No support subscription active. Support is optional.</p>
                  {isOwner && (
                    <Button onClick={() => setShowActivateDialog(true)} className="mt-4 bg-blue-600">
                      Activate Support
                    </Button>
                  )}
                </div>
              )}

              {/* What's Included */}
              <div className="mt-6">
                <h3 className="text-white font-semibold mb-3">Support Includes:</h3>
                <div className="grid grid-cols-2 gap-2">
                  {['Security Patches', 'Version Upgrades', 'Bug Fixes', 'Email Support', 'Migration Tools', 'API Connectors'].map(item => (
                    <div key={item} className="flex items-center gap-2">
                      {supportStatus?.is_support_active ? (
                        <CheckCircle className="w-4 h-4 text-green-400" />
                      ) : (
                        <Lock className="w-4 h-4 text-gray-500" />
                      )}
                      <span className={supportStatus?.is_support_active ? 'text-white' : 'text-gray-500'}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Source Code Transfer Tab */}
        <TabsContent value="transfer" className="space-y-4">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Package className="w-5 h-5 text-blue-400" />
                Source Code Transfer Record
              </CardTitle>
              <CardDescription>Track IP transfer with hash verification and signed confirmation</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {license ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-gray-400 text-sm">Transfer Status</p>
                      <Badge className={
                        license.source_code_transfer_status === 'confirmed' ? 'bg-green-600' :
                        license.source_code_transfer_status === 'delivered' ? 'bg-blue-600' : 'bg-gray-600'
                      }>
                        {license.source_code_transfer_status || 'Pending'}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Transfer Date</p>
                      <p className="text-white">{license.source_code_transfer_date?.slice(0, 10) || 'Not recorded'}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Version</p>
                      <p className="text-white">{license.source_code_version || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Package Hash (SHA256)</p>
                      <p className="text-white font-mono text-xs truncate">{license.source_code_package_hash || 'Not recorded'}</p>
                    </div>
                  </div>

                  {license.source_code_transfer_status === 'confirmed' && (
                    <div className="bg-green-900/30 border border-green-700 rounded-lg p-4 mt-4">
                      <div className="flex items-center gap-2 text-green-400">
                        <CheckSquare className="w-5 h-5" />
                        <span className="font-semibold">Source Code Receipt Confirmed</span>
                      </div>
                      <p className="text-gray-300 text-sm mt-1">
                        Confirmed by: {license.source_code_confirmed_by} on {license.source_code_confirmed_at?.slice(0, 10)}
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 mt-4">
                    {isOwner && license.source_code_transfer_status !== 'confirmed' && (
                      <>
                        {license.source_code_transfer_status === 'pending' && (
                          <Button onClick={() => setShowTransferDialog(true)} variant="outline">
                            <Truck className="w-4 h-4 mr-2" />
                            Record Transfer
                          </Button>
                        )}
                        {license.source_code_transfer_status === 'delivered' && (
                          <Button onClick={confirmTransfer} className="bg-green-600 hover:bg-green-700" disabled={actionLoading}>
                            <FileCheck className="w-4 h-4 mr-2" />
                            Confirm Receipt
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-gray-400">No license data available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Commercial Validation Tab - Phase 2 */}
        {isOwner && (
          <TabsContent value="validation" className="space-y-4">
            {/* Enforcement Validation Section */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <ShieldAlert className="w-5 h-5 text-orange-400" />
                      Enforcement Logic Validation
                    </CardTitle>
                    <CardDescription>Simulate and validate commercial enforcement across all scenarios</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={runEnforcementValidation} disabled={actionLoading} className="bg-orange-600 hover:bg-orange-700">
                      <Play className="w-4 h-4 mr-2" />
                      Run Validation
                    </Button>
                    <Button onClick={downloadValidationPDF} disabled={actionLoading} variant="outline">
                      <FileDown className="w-4 h-4 mr-2" />
                      Download PDF Report
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {validationResult ? (
                  <div className="space-y-4">
                    {/* Validation Summary */}
                    <div className={`p-4 rounded-lg border ${
                      validationResult.summary?.enforcement_status === 'VERIFIED' 
                        ? 'bg-green-900/30 border-green-700' 
                        : 'bg-red-900/30 border-red-700'
                    }`}>
                      <div className="flex items-center gap-3">
                        {validationResult.summary?.enforcement_status === 'VERIFIED' ? (
                          <CheckCircle className="w-8 h-8 text-green-400" />
                        ) : (
                          <AlertOctagon className="w-8 h-8 text-red-400" />
                        )}
                        <div>
                          <p className={`font-bold text-lg ${
                            validationResult.summary?.enforcement_status === 'VERIFIED' 
                              ? 'text-green-400' 
                              : 'text-red-400'
                          }`}>
                            {validationResult.summary?.enforcement_status === 'VERIFIED' 
                              ? 'All Enforcement Checks Passed' 
                              : 'Enforcement Issues Detected'}
                          </p>
                          <p className="text-gray-400 text-sm">
                            Report ID: {validationResult.report_id} • 
                            Pass Rate: {validationResult.summary?.pass_rate}%
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Test Results Grid */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-gray-900 p-4 rounded-lg text-center">
                        <p className="text-gray-400 text-sm">Total Tests</p>
                        <p className="text-2xl font-bold text-white">{validationResult.summary?.total_tests || 0}</p>
                      </div>
                      <div className="bg-gray-900 p-4 rounded-lg text-center">
                        <p className="text-gray-400 text-sm">Passed</p>
                        <p className="text-2xl font-bold text-green-400">{validationResult.passed || 0}</p>
                      </div>
                      <div className="bg-gray-900 p-4 rounded-lg text-center">
                        <p className="text-gray-400 text-sm">Failed</p>
                        <p className="text-2xl font-bold text-red-400">{validationResult.failed || 0}</p>
                      </div>
                    </div>

                    {/* Scenario Breakdown */}
                    <div className="space-y-3">
                      <h4 className="text-white font-semibold">Scenario Results</h4>
                      {validationResult.scenarios?.map((scenario, idx) => (
                        <div key={idx} className="bg-gray-900 p-3 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-white font-medium">{scenario.name}</p>
                              <p className="text-gray-400 text-xs">{scenario.description}</p>
                            </div>
                            <Badge className={scenario.tests_failed === 0 ? 'bg-green-600' : 'bg-red-600'}>
                              {scenario.tests_passed}/{scenario.tests_passed + scenario.tests_failed} Passed
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <ShieldAlert className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Click "Run Validation" to simulate enforcement scenarios</p>
                    <p className="text-sm mt-2">This will test: Active Support, Expired Support, Reactivation, Tamper Detection</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Commercial Risk KPIs Section */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Target className="w-5 h-5 text-purple-400" />
                      Commercial Risk Dashboard
                    </CardTitle>
                    <CardDescription>Key performance indicators for revenue protection</CardDescription>
                  </div>
                  <Button onClick={fetchRiskKpis} disabled={actionLoading} variant="outline" size="sm">
                    <RefreshCw className={`w-4 h-4 mr-2 ${actionLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {riskKpis ? (
                  <div className="space-y-4">
                    {/* Risk Assessment Banner */}
                    <div className={`p-4 rounded-lg border ${
                      riskKpis.risk_assessment?.level === 'HIGH' ? 'bg-red-900/30 border-red-700' :
                      riskKpis.risk_assessment?.level === 'MEDIUM' ? 'bg-yellow-900/30 border-yellow-700' :
                      'bg-green-900/30 border-green-700'
                    }`}>
                      <div className="flex items-center gap-2">
                        <AlertOctagon className={`w-5 h-5 ${
                          riskKpis.risk_assessment?.level === 'HIGH' ? 'text-red-400' :
                          riskKpis.risk_assessment?.level === 'MEDIUM' ? 'text-yellow-400' :
                          'text-green-400'
                        }`} />
                        <span className="font-semibold text-white">Risk Level: {riskKpis.risk_assessment?.level}</span>
                      </div>
                      <p className="text-gray-300 text-sm mt-1">{riskKpis.risk_assessment?.recommendation}</p>
                    </div>

                    {/* KPI Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* KPI 1: % Without Support */}
                      <Card className="bg-gray-900 border-gray-700">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <Percent className="w-8 h-8 text-orange-400" />
                            <div>
                              <p className="text-gray-400 text-xs">{riskKpis.kpis?.licenses_without_support_pct?.label}</p>
                              <p className="text-2xl font-bold text-orange-400">{riskKpis.kpis?.licenses_without_support_pct?.value}%</p>
                              <p className="text-gray-500 text-xs">{riskKpis.kpis?.licenses_without_support_pct?.description}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* KPI 2: Reactivation Opportunity */}
                      <Card className="bg-gray-900 border-gray-700">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <Wallet className="w-8 h-8 text-green-400" />
                            <div>
                              <p className="text-gray-400 text-xs">{riskKpis.kpis?.reactivation_opportunity?.label}</p>
                              <p className="text-2xl font-bold text-green-400">
                                RM {(riskKpis.kpis?.reactivation_opportunity?.value || 0).toLocaleString()}
                              </p>
                              <p className="text-gray-500 text-xs">
                                {riskKpis.kpis?.reactivation_opportunity?.expired_licenses_count} expired licenses
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* KPI 3: Support Dependency Ratio */}
                      <Card className="bg-gray-900 border-gray-700">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <PieChart className="w-8 h-8 text-blue-400" />
                            <div>
                              <p className="text-gray-400 text-xs">{riskKpis.kpis?.support_dependency_ratio?.label}</p>
                              <p className="text-2xl font-bold text-blue-400">
                                {((riskKpis.kpis?.support_dependency_ratio?.value || 0) * 100).toFixed(1)}%
                              </p>
                              <p className="text-gray-500 text-xs">{riskKpis.kpis?.support_dependency_ratio?.benchmark}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Button onClick={fetchRiskKpis} className="bg-purple-600 hover:bg-purple-700">
                      <Target className="w-4 h-4 mr-2" />
                      Load Risk KPIs
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Revenue Forecast Section */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-green-400" />
                      12-Month Revenue Forecast
                    </CardTitle>
                    <CardDescription>Support revenue projections with best/expected/worst scenarios</CardDescription>
                  </div>
                  <Button onClick={fetchRevenueForecast} disabled={actionLoading} variant="outline" size="sm">
                    <RefreshCw className={`w-4 h-4 mr-2 ${actionLoading ? 'animate-spin' : ''}`} />
                    Generate Forecast
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {revenueForecast ? (
                  <div className="space-y-4">
                    {/* Scenario Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Best Case */}
                      <Card className="bg-green-900/20 border-green-700">
                        <CardContent className="p-4 text-center">
                          <p className="text-green-400 text-sm font-semibold">Best Case</p>
                          <p className="text-2xl font-bold text-green-400">
                            RM {(revenueForecast.scenarios?.best?.total_projected_revenue || 0).toLocaleString()}
                          </p>
                          <p className="text-gray-400 text-xs">
                            {revenueForecast.scenarios?.best?.renewal_rate} renewal rate
                          </p>
                        </CardContent>
                      </Card>

                      {/* Expected Case */}
                      <Card className="bg-blue-900/20 border-blue-700">
                        <CardContent className="p-4 text-center">
                          <p className="text-blue-400 text-sm font-semibold">Expected</p>
                          <p className="text-2xl font-bold text-blue-400">
                            RM {(revenueForecast.scenarios?.expected?.total_projected_revenue || 0).toLocaleString()}
                          </p>
                          <p className="text-gray-400 text-xs">
                            {revenueForecast.scenarios?.expected?.renewal_rate} renewal rate
                          </p>
                        </CardContent>
                      </Card>

                      {/* Worst Case */}
                      <Card className="bg-red-900/20 border-red-700">
                        <CardContent className="p-4 text-center">
                          <p className="text-red-400 text-sm font-semibold">Worst Case</p>
                          <p className="text-2xl font-bold text-red-400">
                            RM {(revenueForecast.scenarios?.worst?.total_projected_revenue || 0).toLocaleString()}
                          </p>
                          <p className="text-gray-400 text-xs">
                            {revenueForecast.scenarios?.worst?.renewal_rate} renewal rate
                          </p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Monthly Breakdown Chart */}
                    <div className="bg-gray-900 p-4 rounded-lg">
                      <h4 className="text-white font-semibold mb-4">Monthly Revenue Projection</h4>
                      <div className="overflow-x-auto">
                        <div className="flex gap-2 min-w-max">
                          {revenueForecast.monthly_breakdown?.map((month, idx) => (
                            <div key={idx} className="bg-gray-800 p-3 rounded-lg min-w-[120px] text-center">
                              <p className="text-gray-400 text-xs">{month.month_name?.slice(0, 3)}</p>
                              <div className="mt-2 space-y-1">
                                <div className="h-24 flex flex-col justify-end gap-1">
                                  <div 
                                    className="bg-green-600/50 rounded-t" 
                                    style={{ height: `${Math.min(100, (month.projected_best || 0) / 1000)}%` }}
                                  ></div>
                                  <div 
                                    className="bg-blue-600/50 rounded-t" 
                                    style={{ height: `${Math.min(100, (month.projected_expected || 0) / 1000)}%` }}
                                  ></div>
                                  <div 
                                    className="bg-red-600/50 rounded-t" 
                                    style={{ height: `${Math.min(100, (month.projected_worst || 0) / 1000)}%` }}
                                  ></div>
                                </div>
                                <p className="text-white text-xs font-semibold">
                                  RM {((month.expiring_value || 0) / 1000).toFixed(0)}k
                                </p>
                                <p className="text-gray-500 text-xs">
                                  {month.expiring_subscriptions} expiring
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-4 mt-4 justify-center">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-green-600 rounded"></div>
                          <span className="text-gray-400 text-xs">Best</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-blue-600 rounded"></div>
                          <span className="text-gray-400 text-xs">Expected</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-red-600 rounded"></div>
                          <span className="text-gray-400 text-xs">Worst</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Button onClick={fetchRevenueForecast} className="bg-green-600 hover:bg-green-700">
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Generate 12-Month Forecast
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Download Control & Audit Export */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Download Stats */}
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2 text-lg">
                    <Download className="w-5 h-5 text-blue-400" />
                    Source Code Download Control
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {downloadStats ? (
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-400">License Tier</span>
                        <Badge className="bg-purple-600">{downloadStats.license_tier}</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Downloads Used</span>
                        <span className="text-white">{downloadStats.total_downloads} / {downloadStats.max_downloads}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Remaining</span>
                        <span className="text-green-400 font-semibold">{downloadStats.remaining_downloads}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Blocked Attempts</span>
                        <span className="text-red-400">{downloadStats.blocked_attempts}</span>
                      </div>
                      {/* Progress Bar */}
                      <div className="mt-3">
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              downloadStats.total_downloads >= downloadStats.max_downloads ? 'bg-red-500' :
                              downloadStats.total_downloads >= downloadStats.max_downloads * 0.8 ? 'bg-yellow-500' :
                              'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(100, (downloadStats.total_downloads / downloadStats.max_downloads) * 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Button onClick={fetchDownloadStats} variant="outline" className="w-full">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Load Download Stats
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Audit Log Export */}
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2 text-lg">
                    <FileDown className="w-5 h-5 text-orange-400" />
                    Audit Log Export
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Button 
                      onClick={() => exportAuditLog('download')} 
                      disabled={actionLoading}
                      variant="outline" 
                      className="w-full justify-start"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export Download Logs
                    </Button>
                    <Button 
                      onClick={() => exportAuditLog('enforcement')} 
                      disabled={actionLoading}
                      variant="outline" 
                      className="w-full justify-start"
                    >
                      <ShieldAlert className="w-4 h-4 mr-2" />
                      Export Enforcement Logs
                    </Button>
                    <Button 
                      onClick={() => exportAuditLog('tamper')} 
                      disabled={actionLoading}
                      variant="outline" 
                      className="w-full justify-start"
                    >
                      <AlertOctagon className="w-4 h-4 mr-2" />
                      Export Tamper Logs
                    </Button>
                    <Button 
                      onClick={() => exportAuditLog('full')} 
                      disabled={actionLoading}
                      className="w-full justify-start bg-orange-600 hover:bg-orange-700"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Export Full Audit Report
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}

        {/* Sales Enablement Tab */}
        {isOwner && (
          <TabsContent value="sales" className="space-y-4">
            {/* Commercial Health Score */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Target className="w-5 h-5 text-green-400" />
                      Commercial Health Score
                    </CardTitle>
                    <CardDescription>Executive dashboard indicator (0-100)</CardDescription>
                  </div>
                  <Button onClick={fetchCommercialHealth} disabled={actionLoading} variant="outline" size="sm">
                    <RefreshCw className={`w-4 h-4 mr-2 ${actionLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {commercialHealth ? (
                  <div className="space-y-6">
                    {/* Large Gauge Indicator */}
                    <div className="flex items-center justify-center">
                      <div className={`relative w-48 h-48 rounded-full border-8 ${
                        commercialHealth.color === 'green' ? 'border-green-500' :
                        commercialHealth.color === 'amber' ? 'border-yellow-500' :
                        'border-red-500'
                      } flex items-center justify-center`}>
                        <div className="text-center">
                          <p className={`text-5xl font-bold ${
                            commercialHealth.color === 'green' ? 'text-green-400' :
                            commercialHealth.color === 'amber' ? 'text-yellow-400' :
                            'text-red-400'
                          }`}>{commercialHealth.health_score}</p>
                          <p className="text-gray-400 text-sm">/ {commercialHealth.max_score}</p>
                          <Badge className={`mt-2 ${
                            commercialHealth.color === 'green' ? 'bg-green-600' :
                            commercialHealth.color === 'amber' ? 'bg-yellow-600' :
                            'bg-red-600'
                          }`}>{commercialHealth.status}</Badge>
                        </div>
                      </div>
                    </div>

                    {/* Recommendation */}
                    <div className="bg-gray-900 p-4 rounded-lg">
                      <p className="text-gray-300 text-sm">{commercialHealth.recommendation}</p>
                    </div>

                    {/* Component Breakdown */}
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                      {Object.entries(commercialHealth.components || {}).map(([key, comp]) => (
                        <div key={key} className="bg-gray-900 p-3 rounded-lg text-center">
                          <p className="text-gray-400 text-xs capitalize">{key.replace(/_/g, ' ')}</p>
                          <p className="text-xl font-bold text-white">{comp.score}/{comp.max_score}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Button onClick={fetchCommercialHealth} className="bg-green-600 hover:bg-green-700">
                      <Target className="w-4 h-4 mr-2" />
                      Load Commercial Health Score
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Proposal Generator */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-400" />
                  Commercial Proposal Generator
                </CardTitle>
                <CardDescription>Generate board-ready commercial proposals</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <Label className="text-gray-300">Client Name</Label>
                      <Input 
                        value={proposalForm.client_name}
                        onChange={(e) => setProposalForm({...proposalForm, client_name: e.target.value})}
                        placeholder="Company Name"
                        className="bg-gray-900 border-gray-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Legal Entity</Label>
                      <Input 
                        value={proposalForm.client_legal_entity}
                        onChange={(e) => setProposalForm({...proposalForm, client_legal_entity: e.target.value})}
                        placeholder="Full Legal Name (e.g., ABC Sdn Bhd)"
                        className="bg-gray-900 border-gray-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">License Tier</Label>
                      <Select value={proposalForm.license_tier} onValueChange={(v) => setProposalForm({...proposalForm, license_tier: v})}>
                        <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="small_business">Small Business (≤50 employees) - RM 35,000</SelectItem>
                          <SelectItem value="professional">Professional (≤300 employees) - RM 55,000</SelectItem>
                          <SelectItem value="enterprise">Enterprise (≤1000 employees) - RM 95,000</SelectItem>
                          <SelectItem value="enterprise_plus">Enterprise Plus (Unlimited) - RM 150,000</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-gray-300">Contact Person</Label>
                      <Input 
                        value={proposalForm.contact_person}
                        onChange={(e) => setProposalForm({...proposalForm, contact_person: e.target.value})}
                        placeholder="Primary Contact Name"
                        className="bg-gray-900 border-gray-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Contact Email</Label>
                      <Input 
                        value={proposalForm.contact_email}
                        onChange={(e) => setProposalForm({...proposalForm, contact_email: e.target.value})}
                        placeholder="contact@company.com"
                        className="bg-gray-900 border-gray-700 text-white"
                      />
                    </div>
                    <Button onClick={generateProposal} disabled={actionLoading} className="w-full bg-blue-600 hover:bg-blue-700 mt-4">
                      <FileDown className="w-4 h-4 mr-2" />
                      Generate & Download Proposal
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pricing Stress-Test Simulator */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-purple-400" />
                  Pricing Stress-Test Engine
                </CardTitle>
                <CardDescription>Validate pricing sustainability with 3-year projections</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <Label className="text-gray-300">License Price (RM)</Label>
                    <Input 
                      type="number"
                      value={simulatorInputs.license_price}
                      onChange={(e) => setSimulatorInputs({...simulatorInputs, license_price: parseInt(e.target.value)})}
                      className="bg-gray-900 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Support Rate (%)</Label>
                    <Input 
                      type="number"
                      step="0.01"
                      value={simulatorInputs.support_rate * 100}
                      onChange={(e) => setSimulatorInputs({...simulatorInputs, support_rate: parseFloat(e.target.value) / 100})}
                      className="bg-gray-900 border-gray-700 text-white"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={runPricingSimulation} disabled={actionLoading} className="w-full bg-purple-600 hover:bg-purple-700">
                      <BarChart3 className="w-4 h-4 mr-2" />
                      Run Simulation
                    </Button>
                  </div>
                </div>

                {pricingSimulation && (
                  <div className="space-y-4 mt-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <Card className="bg-gray-900 border-gray-700">
                        <CardContent className="p-4 text-center">
                          <p className="text-gray-400 text-xs">3-Year Total Revenue</p>
                          <p className="text-2xl font-bold text-green-400">
                            RM {(pricingSimulation.summary?.total_3year_revenue || 0).toLocaleString()}
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="bg-gray-900 border-gray-700">
                        <CardContent className="p-4 text-center">
                          <p className="text-gray-400 text-xs">Support Dependency</p>
                          <p className="text-2xl font-bold text-blue-400">
                            {pricingSimulation.summary?.support_dependency_percentage || '0%'}
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="bg-gray-900 border-gray-700">
                        <CardContent className="p-4 text-center">
                          <p className="text-gray-400 text-xs">Revenue Volatility</p>
                          <p className="text-2xl font-bold text-yellow-400">
                            {pricingSimulation.summary?.revenue_volatility_index || 0}
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="bg-gray-900 border-gray-700">
                        <CardContent className="p-4 text-center">
                          <p className="text-gray-400 text-xs">Break-Even</p>
                          <p className="text-2xl font-bold text-purple-400">
                            {pricingSimulation.summary?.break_even_status || 'N/A'}
                          </p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Year-by-Year Projection */}
                    <div className="bg-gray-900 p-4 rounded-lg">
                      <h4 className="text-white font-semibold mb-3">Year-by-Year Projection</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-gray-400 border-b border-gray-700">
                              <th className="text-left p-2">Year</th>
                              <th className="text-right p-2">New Licenses</th>
                              <th className="text-right p-2">License Revenue</th>
                              <th className="text-right p-2">Support Revenue</th>
                              <th className="text-right p-2">Total Revenue</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pricingSimulation.projections?.map((proj, idx) => (
                              <tr key={idx} className="border-b border-gray-800">
                                <td className="p-2 text-white">Year {proj.year}</td>
                                <td className="p-2 text-right text-gray-300">{proj.new_licenses}</td>
                                <td className="p-2 text-right text-blue-400">RM {proj.license_revenue?.toLocaleString()}</td>
                                <td className="p-2 text-right text-green-400">RM {proj.support_revenue?.toLocaleString()}</td>
                                <td className="p-2 text-right text-white font-semibold">RM {proj.total_revenue?.toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Risk Assessment */}
                    <div className={`p-4 rounded-lg border ${
                      pricingSimulation.risk_assessment?.level === 'LOW' ? 'bg-green-900/30 border-green-700' :
                      pricingSimulation.risk_assessment?.level === 'MEDIUM' ? 'bg-yellow-900/30 border-yellow-700' :
                      'bg-red-900/30 border-red-700'
                    }`}>
                      <p className="font-semibold text-white">Risk Level: {pricingSimulation.risk_assessment?.level}</p>
                      <p className="text-gray-300 text-sm mt-1">{pricingSimulation.risk_assessment?.recommendation}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Sales Tools */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Sales Scripts */}
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white flex items-center gap-2 text-lg">
                      <Briefcase className="w-5 h-5 text-orange-400" />
                      Sales Scripts
                    </CardTitle>
                    <Button onClick={fetchSalesScripts} disabled={actionLoading} variant="outline" size="sm">
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {salesScripts ? (
                    <div className="space-y-3">
                      <div className="bg-gray-900 p-3 rounded-lg">
                        <p className="text-white font-medium">2-Minute Pitch</p>
                        <p className="text-gray-400 text-xs mt-1">Quick elevator pitch for initial conversations</p>
                      </div>
                      <div className="bg-gray-900 p-3 rounded-lg">
                        <p className="text-white font-medium">5-Minute Deep Dive</p>
                        <p className="text-gray-400 text-xs mt-1">Comprehensive explanation of the PSCL model</p>
                      </div>
                      <div className="bg-gray-900 p-3 rounded-lg">
                        <p className="text-white font-medium">Objection Handling</p>
                        <p className="text-gray-400 text-xs mt-1">4 key objections with prepared responses</p>
                      </div>
                      <Button onClick={downloadSalesPlaybook} disabled={actionLoading} className="w-full bg-orange-600 hover:bg-orange-700">
                        <FileDown className="w-4 h-4 mr-2" />
                        Download Sales Playbook (PDF)
                      </Button>
                    </div>
                  ) : (
                    <Button onClick={fetchSalesScripts} variant="outline" className="w-full">
                      Load Sales Scripts
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Comparison & Positioning */}
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2 text-lg">
                    <BarChart3 className="w-5 h-5 text-cyan-400" />
                    Positioning Documents
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="bg-gray-900 p-3 rounded-lg">
                    <p className="text-white font-medium">Licensing Model Comparison</p>
                    <p className="text-gray-400 text-xs mt-1">PSCL vs SaaS vs White Label vs Full IP Sale</p>
                    <p className="text-gray-500 text-xs">Dynamically generated with live pricing</p>
                  </div>
                  <Button onClick={downloadComparisonSheet} disabled={actionLoading} className="w-full bg-cyan-600 hover:bg-cyan-700">
                    <FileDown className="w-4 h-4 mr-2" />
                    Generate Comparison Sheet (PDF)
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}

        {/* Revenue Execution Tab - Phase 4 */}
        {isOwner && (
          <TabsContent value="revenue" className="space-y-4">
            {/* Revenue Readiness Score */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Zap className="w-5 h-5 text-yellow-400" />
                      Executive Revenue Readiness Score
                    </CardTitle>
                    <CardDescription>Board-level composite score (0-100)</CardDescription>
                  </div>
                  <Button onClick={fetchRevenueReadiness} disabled={actionLoading} variant="outline" size="sm">
                    <RefreshCw className={`w-4 h-4 mr-2 ${actionLoading ? 'animate-spin' : ''}`} />
                    Calculate
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {revenueReadiness ? (
                  <div className="space-y-6">
                    {/* Large Score Display */}
                    <div className="flex items-center justify-center">
                      <div className={`relative w-40 h-40 rounded-full border-8 ${
                        revenueReadiness.color === 'green' ? 'border-green-500' :
                        revenueReadiness.color === 'amber' ? 'border-yellow-500' :
                        'border-red-500'
                      } flex items-center justify-center`}>
                        <div className="text-center">
                          <p className={`text-4xl font-bold ${
                            revenueReadiness.color === 'green' ? 'text-green-400' :
                            revenueReadiness.color === 'amber' ? 'text-yellow-400' :
                            'text-red-400'
                          }`}>{revenueReadiness.readiness_score}</p>
                          <p className="text-gray-400 text-xs">/ {revenueReadiness.max_score}</p>
                          <Badge className={`mt-1 text-xs ${
                            revenueReadiness.color === 'green' ? 'bg-green-600' :
                            revenueReadiness.color === 'amber' ? 'bg-yellow-600' :
                            'bg-red-600'
                          }`}>{revenueReadiness.status}</Badge>
                        </div>
                      </div>
                    </div>

                    {/* Component Breakdown */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                      {Object.entries(revenueReadiness.components || {}).map(([key, comp]) => (
                        <div key={key} className="bg-gray-900 p-3 rounded-lg text-center">
                          <p className="text-gray-400 text-xs capitalize">{key.replace(/_/g, ' ')}</p>
                          <p className="text-lg font-bold text-white">{comp.score}/{comp.max_score}</p>
                        </div>
                      ))}
                    </div>

                    {/* Strengths & Concerns */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-green-900/20 border border-green-700 rounded-lg p-4">
                        <h4 className="text-green-400 font-semibold mb-2">Strengths</h4>
                        <ul className="space-y-1">
                          {revenueReadiness.strengths?.map((s, i) => (
                            <li key={i} className="text-gray-300 text-sm flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                              {s}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
                        <h4 className="text-red-400 font-semibold mb-2">Concerns & Actions</h4>
                        <ul className="space-y-1">
                          {revenueReadiness.concerns?.map((c, i) => (
                            <li key={i} className="text-gray-300 text-sm flex items-start gap-2">
                              <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                              {c}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Button onClick={fetchRevenueReadiness} className="bg-yellow-600 hover:bg-yellow-700">
                      <Zap className="w-4 h-4 mr-2" />
                      Calculate Revenue Readiness
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Deal Conversion Analytics */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <LineChart className="w-5 h-5 text-blue-400" />
                      Deal Conversion Analytics
                    </CardTitle>
                    <CardDescription>Sales funnel metrics and conversion rates</CardDescription>
                  </div>
                  <Button onClick={fetchDealMetrics} disabled={actionLoading} variant="outline" size="sm">
                    <RefreshCw className={`w-4 h-4 mr-2 ${actionLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {dealMetrics ? (
                  <div className="space-y-4">
                    {/* Funnel Metrics */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <Card className="bg-gray-900 border-gray-700">
                        <CardContent className="p-4 text-center">
                          <p className="text-gray-400 text-xs">Generated</p>
                          <p className="text-2xl font-bold text-white">{dealMetrics.total_proposals_generated}</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-gray-900 border-gray-700">
                        <CardContent className="p-4 text-center">
                          <p className="text-gray-400 text-xs">Sent</p>
                          <p className="text-2xl font-bold text-blue-400">{dealMetrics.total_proposals_sent}</p>
                          <p className="text-xs text-gray-500">{dealMetrics.sent_rate}%</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-gray-900 border-gray-700">
                        <CardContent className="p-4 text-center">
                          <p className="text-gray-400 text-xs">Viewed</p>
                          <p className="text-2xl font-bold text-purple-400">{dealMetrics.total_proposals_viewed}</p>
                          <p className="text-xs text-gray-500">{dealMetrics.view_rate}%</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-gray-900 border-gray-700">
                        <CardContent className="p-4 text-center">
                          <p className="text-gray-400 text-xs">Won</p>
                          <p className="text-2xl font-bold text-green-400">{dealMetrics.total_proposals_won}</p>
                          <p className="text-xs text-gray-500">{dealMetrics.close_rate}% close rate</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-gray-900 border-gray-700">
                        <CardContent className="p-4 text-center">
                          <p className="text-gray-400 text-xs">Avg Cycle</p>
                          <p className="text-2xl font-bold text-orange-400">{dealMetrics.avg_sales_cycle_days}</p>
                          <p className="text-xs text-gray-500">days</p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Revenue Breakdown */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-gray-900 p-3 rounded-lg">
                        <p className="text-gray-400 text-xs">Total Pipeline</p>
                        <p className="text-lg font-bold text-white">RM {dealMetrics.total_proposal_value?.toLocaleString()}</p>
                      </div>
                      <div className="bg-gray-900 p-3 rounded-lg">
                        <p className="text-gray-400 text-xs">Won Revenue</p>
                        <p className="text-lg font-bold text-green-400">RM {dealMetrics.total_won_value?.toLocaleString()}</p>
                      </div>
                      <div className="bg-gray-900 p-3 rounded-lg">
                        <p className="text-gray-400 text-xs">Pending</p>
                        <p className="text-lg font-bold text-yellow-400">RM {dealMetrics.total_pending_value?.toLocaleString()}</p>
                      </div>
                      <div className="bg-gray-900 p-3 rounded-lg">
                        <p className="text-gray-400 text-xs">Hot Leads</p>
                        <p className="text-lg font-bold text-orange-400">{dealMetrics.total_hot_leads}</p>
                      </div>
                    </div>

                    {/* Tier Breakdown */}
                    {dealMetrics.metrics_by_tier && Object.keys(dealMetrics.metrics_by_tier).length > 0 && (
                      <div className="bg-gray-900 p-4 rounded-lg">
                        <h4 className="text-white font-semibold mb-3">By License Tier</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {Object.entries(dealMetrics.metrics_by_tier).map(([tier, data]) => (
                            <div key={tier} className="bg-gray-800 p-3 rounded">
                              <p className="text-gray-400 text-xs capitalize">{tier.replace(/_/g, ' ')}</p>
                              <p className="text-white font-semibold">{data.generated} proposals</p>
                              <p className="text-green-400 text-sm">{data.conversion_rate}% conversion</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Button onClick={fetchDealMetrics} className="bg-blue-600 hover:bg-blue-700">
                      <LineChart className="w-4 h-4 mr-2" />
                      Load Deal Metrics
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Hot Leads */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Flame className="w-5 h-5 text-orange-400" />
                      Hot Leads
                    </CardTitle>
                    <CardDescription>Proposals with 3+ views in 7 days</CardDescription>
                  </div>
                  <Button onClick={fetchHotLeads} disabled={actionLoading} variant="outline" size="sm">
                    <RefreshCw className={`w-4 h-4 mr-2 ${actionLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {hotLeads.length > 0 ? (
                  <div className="space-y-2">
                    {hotLeads.map((lead, i) => (
                      <div key={i} className="bg-gray-900 p-3 rounded-lg flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium">{lead.client_name || lead.customer_legal_entity}</p>
                          <p className="text-gray-400 text-sm">{lead.license_tier} • {lead.total_views} views</p>
                        </div>
                        <div className="text-right">
                          <Badge className="bg-orange-600">HOT</Badge>
                          <p className="text-green-400 text-sm mt-1">RM {lead.proposal_value?.toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-400">
                    <Flame className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No hot leads detected yet</p>
                    <p className="text-sm">Proposals become "hot" after 3+ views in 7 days</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Reactivation Campaign Engine */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-green-400" />
                      Reactivation Campaign Engine
                    </CardTitle>
                    <CardDescription>Generate time-limited reactivation offers (30-day expiry)</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={fetchExpiredLicenses} disabled={actionLoading} variant="outline" size="sm">
                      <RefreshCw className={`w-4 h-4 mr-2 ${actionLoading ? 'animate-spin' : ''}`} />
                      Scan Expired
                    </Button>
                    <Button onClick={fetchReactivationOffers} disabled={actionLoading} variant="outline" size="sm">
                      View Offers
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Expired Licenses */}
                  {expiredLicenses.length > 0 && (
                    <div>
                      <h4 className="text-white font-semibold mb-2">Expired Support Licenses</h4>
                      <div className="space-y-2">
                        {expiredLicenses.map((lic, i) => (
                          <div key={i} className="bg-gray-900 p-3 rounded-lg flex items-center justify-between">
                            <div>
                              <p className="text-white font-medium">{lic.customer_legal_entity}</p>
                              <p className="text-gray-400 text-sm">
                                Expired {lic.days_lapsed} days ago • {lic.license_tier}
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <p className="text-orange-400 font-semibold">RM {lic.total_reactivation_cost?.toLocaleString()}</p>
                                <p className="text-gray-500 text-xs">reactivation cost</p>
                              </div>
                              <Button 
                                onClick={() => generateReactivationOffer(lic)}
                                disabled={actionLoading}
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <Send className="w-4 h-4 mr-1" />
                                Generate Offer
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Active Offers */}
                  {reactivationOffers.length > 0 && (
                    <div>
                      <h4 className="text-white font-semibold mb-2">Active Reactivation Offers</h4>
                      <div className="space-y-2">
                        {reactivationOffers.map((offer, i) => (
                          <div key={i} className="bg-gray-900 p-3 rounded-lg flex items-center justify-between">
                            <div>
                              <p className="text-white font-medium">{offer.customer_legal_entity}</p>
                              <p className="text-gray-400 text-sm">
                                {offer.offer_id} • {offer.days_until_expiry} days left
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge className={
                                offer.status === 'sent' ? 'bg-blue-600' :
                                offer.status === 'viewed' ? 'bg-purple-600' :
                                offer.status === 'accepted' ? 'bg-green-600' :
                                offer.status === 'expired' ? 'bg-red-600' :
                                'bg-gray-600'
                              }>{offer.status}</Badge>
                              <p className="text-green-400 font-semibold">RM {offer.final_offer_amount?.toLocaleString()}</p>
                              {offer.status === 'draft' && (
                                <Button 
                                  onClick={() => sendReactivationOffer(offer.offer_id)}
                                  disabled={actionLoading}
                                  size="sm"
                                  className="bg-blue-600"
                                >
                                  <Send className="w-4 h-4 mr-1" />
                                  Send
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {expiredLicenses.length === 0 && reactivationOffers.length === 0 && (
                    <div className="text-center py-6 text-gray-400">
                      <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>Click "Scan Expired" to find reactivation opportunities</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Market Reality Simulation */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-purple-400" />
                      Market Reality Simulation
                    </CardTitle>
                    <CardDescription>Revenue projections for 5, 10, 25, 50 new clients</CardDescription>
                  </div>
                  <Button onClick={fetchMarketSimulation} disabled={actionLoading} className="bg-purple-600 hover:bg-purple-700">
                    <Play className="w-4 h-4 mr-2" />
                    Run Simulation
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {marketSimulation ? (
                  <div className="space-y-4">
                    {/* Key Insights */}
                    <div className="bg-purple-900/20 border border-purple-700 rounded-lg p-4">
                      <h4 className="text-purple-400 font-semibold mb-2">Key Insights</h4>
                      <ul className="space-y-1">
                        {marketSimulation.key_insights?.map((insight, i) => (
                          <li key={i} className="text-gray-300 text-sm flex items-start gap-2">
                            <TrendingUp className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                            {insight}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Scenario Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {marketSimulation.scenarios?.map((scenario, i) => (
                        <Card key={i} className="bg-gray-900 border-gray-700">
                          <CardContent className="p-4">
                            <h4 className="text-white font-semibold text-center mb-2">{scenario.scenario_name}</h4>
                            <div className="space-y-2 text-sm">
                              <div className="text-center">
                                <p className="text-gray-400 text-xs">3-Year Total</p>
                                <p className="text-xl font-bold text-green-400">
                                  RM {(scenario.total_revenue / 1000000).toFixed(1)}M
                                </p>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Y1</span>
                                <span className="text-white">RM {(scenario.year_1_total_revenue / 1000).toFixed(0)}k</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Y2</span>
                                <span className="text-white">RM {(scenario.year_2_total_revenue / 1000).toFixed(0)}k</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Y3</span>
                                <span className="text-white">RM {(scenario.year_3_total_revenue / 1000).toFixed(0)}k</span>
                              </div>
                              <div className="border-t border-gray-700 pt-2 flex justify-between">
                                <span className="text-gray-400">ARR Y3</span>
                                <span className="text-blue-400">RM {(scenario.arr_y3 / 1000).toFixed(0)}k</span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    {/* Assumptions */}
                    <div className="bg-gray-900 p-4 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Simulation Assumptions</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-400">Support Adoption</p>
                          <p className="text-white">{(marketSimulation.base_assumptions?.support_adoption_rate || 0) * 100}%</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Renewal Rate</p>
                          <p className="text-white">{(marketSimulation.base_assumptions?.renewal_rate || 0) * 100}%</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Y2 Growth</p>
                          <p className="text-white">{(marketSimulation.base_assumptions?.growth_rate_y2 || 0) * 100}%</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Y3 Growth</p>
                          <p className="text-white">{(marketSimulation.base_assumptions?.growth_rate_y3 || 0) * 100}%</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <BarChart3 className="w-12 h-12 mx-auto mb-4 text-purple-400 opacity-50" />
                    <p className="text-gray-400 mb-4">Run simulation to see revenue projections</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Commercial Metrics Tab */}
        {isOwner && (
          <TabsContent value="metrics" className="space-y-4">
            {/* Metrics Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-blue-900/50 to-blue-800/30 border-blue-700">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Total Licenses</p>
                      <p className="text-2xl font-bold text-white">{commercialMetrics?.total_pscl_licenses || 0}</p>
                    </div>
                    <Key className="w-8 h-8 text-blue-400 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-green-900/50 to-green-800/30 border-green-700">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Active Support</p>
                      <p className="text-2xl font-bold text-green-400">{commercialMetrics?.active_support_count || 0}</p>
                      <p className="text-xs text-green-300">{commercialMetrics?.active_support_percentage || 0}%</p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-green-400 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-red-900/50 to-red-800/30 border-red-700">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Expired Support</p>
                      <p className="text-2xl font-bold text-red-400">{commercialMetrics?.expired_support_count || 0}</p>
                      <p className="text-xs text-red-300">{commercialMetrics?.expired_support_percentage || 0}%</p>
                    </div>
                    <XCircle className="w-8 h-8 text-red-400 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-purple-900/50 to-purple-800/30 border-purple-700">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Projected Revenue</p>
                      <p className="text-2xl font-bold text-purple-400">RM {(commercialMetrics?.projected_annual_revenue || 0).toLocaleString()}</p>
                      <p className="text-xs text-purple-300">Annual</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-purple-400 opacity-50" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Scheduler Status & Actions */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Activity className="w-5 h-5 text-blue-400" />
                      Revenue Protection Scheduler
                    </CardTitle>
                    <CardDescription>Automated daily enforcement at 02:00 UTC</CardDescription>
                  </div>
                  <Badge className={commercialMetrics?.scheduler_status === 'running' ? 'bg-green-600' : 'bg-gray-600'}>
                    {commercialMetrics?.scheduler_status || 'Unknown'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-gray-900 p-3 rounded-lg">
                    <p className="text-gray-400 text-sm">Upcoming Renewals (90 days)</p>
                    <p className="text-xl font-bold text-yellow-400">{commercialMetrics?.upcoming_renewals_90_days || 0}</p>
                  </div>
                  <div className="bg-gray-900 p-3 rounded-lg">
                    <p className="text-gray-400 text-sm">Enforcement Actions (30d)</p>
                    <p className="text-xl font-bold text-orange-400">{commercialMetrics?.enforcement_actions_30d || 0}</p>
                  </div>
                  <div className="bg-gray-900 p-3 rounded-lg">
                    <p className="text-gray-400 text-sm">Tamper Attempts</p>
                    <p className="text-xl font-bold text-red-400">{commercialMetrics?.tamper_attempts_total || 0}</p>
                  </div>
                </div>

                {commercialMetrics?.last_scheduler_run && (
                  <div className="bg-gray-900 p-3 rounded-lg mb-4">
                    <p className="text-gray-400 text-sm">Last Scheduler Run</p>
                    <p className="text-white">{commercialMetrics.last_scheduler_run.completed_at?.slice(0, 19).replace('T', ' ')}</p>
                    <p className="text-gray-400 text-xs">Status: {commercialMetrics.last_scheduler_run.status}</p>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button onClick={runSchedulerNow} disabled={actionLoading} className="bg-blue-600 hover:bg-blue-700">
                    <Play className="w-4 h-4 mr-2" />
                    Run Scheduler Now
                  </Button>
                  <Button onClick={() => fetchEnforcementLogs()} variant="outline">
                    <Eye className="w-4 h-4 mr-2" />
                    View Enforcement Logs
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Tamper Protection / Integrity Check */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <ShieldAlert className="w-5 h-5 text-orange-400" />
                      Tamper Protection & Integrity
                    </CardTitle>
                    <CardDescription>Verify commercial record integrity</CardDescription>
                  </div>
                  {integrityStatus && (
                    <Badge className={integrityStatus.integrity_verified ? 'bg-green-600' : 'bg-red-600'}>
                      {integrityStatus.integrity_verified ? 'Verified' : 'Issues Found'}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {integrityStatus && (
                  <div className="mb-4">
                    {integrityStatus.integrity_verified ? (
                      <div className="bg-green-900/30 border border-green-700 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-green-400">
                          <CheckCircle className="w-5 h-5" />
                          <span className="font-semibold">All Integrity Checks Passed</span>
                        </div>
                        <p className="text-gray-300 text-sm mt-1">No tampering detected. Last checked: {integrityStatus.checked_at?.slice(0, 19).replace('T', ' ')}</p>
                      </div>
                    ) : (
                      <div className="bg-red-900/30 border border-red-700 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-red-400 mb-2">
                          <AlertTriangle className="w-5 h-5" />
                          <span className="font-semibold">Integrity Issues Detected!</span>
                        </div>
                        {integrityStatus.issues?.map((issue, idx) => (
                          <div key={idx} className="text-sm mt-2">
                            <Badge className={issue.severity === 'critical' ? 'bg-red-600' : 'bg-yellow-600'}>{issue.severity}</Badge>
                            <span className="text-gray-300 ml-2">{issue.message}</span>
                          </div>
                        ))}
                        <p className="text-red-300 text-sm mt-3 font-semibold">{integrityStatus.recommendation}</p>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-3">
                  <Button onClick={fetchIntegrityCheck} disabled={actionLoading} className="bg-orange-600 hover:bg-orange-700">
                    <ShieldAlert className="w-4 h-4 mr-2" />
                    Run Integrity Check
                  </Button>
                  <Button onClick={recalculateHashes} disabled={actionLoading} variant="outline">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Recalculate Hashes
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Enforcement Logs */}
            {enforcementLogs.length > 0 && (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Recent Enforcement Actions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {enforcementLogs.map((log, idx) => (
                      <div key={idx} className="bg-gray-900 p-3 rounded-lg flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm font-semibold">{log.action}</p>
                          <p className="text-gray-400 text-xs">{log.details}</p>
                        </div>
                        <p className="text-gray-500 text-xs">{log.enforced_at?.slice(0, 10)}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tier Breakdown */}
            {commercialMetrics?.tier_breakdown && commercialMetrics.tier_breakdown.length > 0 && (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">License Tier Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {commercialMetrics.tier_breakdown.map((tier, idx) => (
                      <div key={idx} className="bg-gray-900 p-4 rounded-lg text-center">
                        <p className="text-gray-400 text-sm capitalize">{tier._id?.replace('_', ' ')}</p>
                        <p className="text-2xl font-bold text-white">{tier.count}</p>
                        <p className="text-green-400 text-sm">RM {(tier.total_fees || 0).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        )}

        {/* Quotations Tab */}
        {isOwner && (
          <TabsContent value="quotations" className="space-y-4">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white">Quotation History</CardTitle>
                  <Button onClick={() => setShowQuotationDialog(true)} className="bg-blue-600">
                    <FileText className="w-4 h-4 mr-2" />
                    New Quotation
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {quotations.length > 0 ? (
                  <div className="space-y-3">
                    {quotations.map(quote => (
                      <div key={quote.quote_id} className="bg-gray-900 p-4 rounded-lg flex items-center justify-between">
                        <div>
                          <p className="text-white font-semibold">{quote.customer_legal_entity}</p>
                          <p className="text-gray-400 text-sm">{quote.quote_id} • {quote.quote_date?.slice(0, 10)}</p>
                          <p className="text-green-400 font-semibold mt-1">RM {quote.year_one_total?.toLocaleString()}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={
                            quote.status === 'accepted' ? 'bg-green-600' :
                            quote.status === 'expired' ? 'bg-gray-600' : 'bg-blue-600'
                          }>{quote.status}</Badge>
                          <Button variant="ghost" size="sm" onClick={() => downloadQuotationPDF(quote.quote_id)}>
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-center py-4">No quotations created yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Pricing Reference Tab */}
        <TabsContent value="pricing" className="space-y-4">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">PSCL Pricing Reference</CardTitle>
              <CardDescription>Perpetual Source Code License pricing tiers</CardDescription>
            </CardHeader>
            <CardContent>
              {pricing && (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-700">
                          <th className="text-left py-3 text-gray-400">Tier</th>
                          <th className="text-left py-3 text-gray-400">Max Employees</th>
                          <th className="text-right py-3 text-gray-400">License Fee</th>
                          <th className="text-right py-3 text-gray-400">Annual Support</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pricing.pricing_tiers?.map(tier => (
                          <tr key={tier.tier} className="border-b border-gray-800">
                            <td className="py-3 text-white font-semibold">{tier.name}</td>
                            <td className="py-3 text-white">≤ {tier.max_employees}</td>
                            <td className="py-3 text-green-400 text-right font-semibold">RM {tier.license_fee.toLocaleString()}</td>
                            <td className="py-3 text-blue-400 text-right">RM {tier.annual_support_fee.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                    <div className="bg-gray-900 p-4 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Implementation Fees</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Standard Setup</span>
                          <span className="text-white">RM {pricing.implementation_fees?.standard_setup?.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Assisted Migration</span>
                          <span className="text-white">RM {pricing.implementation_fees?.assisted_migration?.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Customization</span>
                          <span className="text-white">RM {pricing.implementation_fees?.customization_hourly}/hour</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-900 p-4 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Key Terms</h4>
                      <div className="space-y-2 text-sm">
                        <p className="text-gray-300">• Support Rate: 20% of license fee annually</p>
                        <p className="text-gray-300">• Reactivation Penalty: {pricing.reactivation_penalty_rate}</p>
                        <p className="text-gray-300">• Penalty Cap: 100% of one year's support</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Quotation Dialog */}
      <Dialog open={showQuotationDialog} onOpenChange={setShowQuotationDialog}>
        <DialogContent className="bg-gray-800 border-gray-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Create Quotation</DialogTitle>
            <DialogDescription>Generate a formal PSCL quotation for a customer</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-white">Customer Legal Entity *</Label>
                <Input 
                  value={quotationForm.customer_legal_entity}
                  onChange={e => setQuotationForm({...quotationForm, customer_legal_entity: e.target.value})}
                  className="bg-gray-900 border-gray-700 text-white"
                  placeholder="Full legal company name"
                />
              </div>
              <div>
                <Label className="text-white">Contact Name *</Label>
                <Input 
                  value={quotationForm.customer_contact_name}
                  onChange={e => setQuotationForm({...quotationForm, customer_contact_name: e.target.value})}
                  className="bg-gray-900 border-gray-700 text-white"
                />
              </div>
              <div>
                <Label className="text-white">Contact Email *</Label>
                <Input 
                  value={quotationForm.customer_contact_email}
                  onChange={e => setQuotationForm({...quotationForm, customer_contact_email: e.target.value})}
                  className="bg-gray-900 border-gray-700 text-white"
                  type="email"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-white">Address</Label>
                <Input 
                  value={quotationForm.customer_address}
                  onChange={e => setQuotationForm({...quotationForm, customer_address: e.target.value})}
                  className="bg-gray-900 border-gray-700 text-white"
                />
              </div>
              <div>
                <Label className="text-white">Employee Count *</Label>
                <Input 
                  value={quotationForm.employee_count}
                  onChange={e => setQuotationForm({...quotationForm, employee_count: parseInt(e.target.value) || 0})}
                  className="bg-gray-900 border-gray-700 text-white"
                  type="number"
                  min="1"
                />
              </div>
              <div>
                <Label className="text-white">Customization Hours</Label>
                <Input 
                  value={quotationForm.customization_hours}
                  onChange={e => setQuotationForm({...quotationForm, customization_hours: parseInt(e.target.value) || 0})}
                  className="bg-gray-900 border-gray-700 text-white"
                  type="number"
                  min="0"
                />
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch 
                  checked={quotationForm.include_migration}
                  onCheckedChange={v => setQuotationForm({...quotationForm, include_migration: v})}
                />
                <Label className="text-white">Include Migration</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch 
                  checked={quotationForm.include_support}
                  onCheckedChange={v => setQuotationForm({...quotationForm, include_support: v})}
                />
                <Label className="text-white">Include Support (Optional)</Label>
              </div>
            </div>

            {/* Preview */}
            {quotationPreview && (
              <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
                <h4 className="text-white font-semibold mb-3">Price Preview</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">License Tier</span>
                    <span className="text-white">{quotationPreview.license_tier_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">License Fee</span>
                    <span className="text-white">RM {quotationPreview.license_fee?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Standard Setup</span>
                    <span className="text-white">RM {quotationPreview.standard_setup_fee?.toLocaleString()}</span>
                  </div>
                  {quotationPreview.assisted_migration_fee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Migration</span>
                      <span className="text-white">RM {quotationPreview.assisted_migration_fee?.toLocaleString()}</span>
                    </div>
                  )}
                  {quotationPreview.customization_fee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Customization</span>
                      <span className="text-white">RM {quotationPreview.customization_fee?.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="border-t border-gray-700 pt-2 flex justify-between">
                    <span className="text-gray-400">One-Time Total</span>
                    <span className="text-green-400 font-semibold">RM {quotationPreview.one_time_total?.toLocaleString()}</span>
                  </div>
                  {quotationPreview.annual_support_fee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Annual Support (Optional)</span>
                      <span className="text-blue-400">RM {quotationPreview.annual_support_fee?.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="border-t border-gray-700 pt-2 flex justify-between">
                    <span className="text-white font-semibold">Year 1 Total</span>
                    <span className="text-green-400 font-bold text-lg">RM {quotationPreview.year_one_total?.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuotationDialog(false)}>Cancel</Button>
            <Button onClick={createQuotation} disabled={actionLoading || !quotationForm.customer_legal_entity} className="bg-blue-600">
              {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Create & Download PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activate Support Dialog */}
      <Dialog open={showActivateDialog} onOpenChange={setShowActivateDialog}>
        <DialogContent className="bg-gray-800 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white">Activate Support Subscription</DialogTitle>
            <DialogDescription>Record payment to activate support for 1 year</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-white">Payment Amount (RM)</Label>
              <Input 
                value={paymentForm.amount}
                onChange={e => setPaymentForm({...paymentForm, amount: parseFloat(e.target.value) || 0})}
                className="bg-gray-900 border-gray-700 text-white"
                type="number"
              />
            </div>
            <div>
              <Label className="text-white">Payment Reference</Label>
              <Input 
                value={paymentForm.reference}
                onChange={e => setPaymentForm({...paymentForm, reference: e.target.value})}
                className="bg-gray-900 border-gray-700 text-white"
                placeholder="Invoice/Receipt number"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActivateDialog(false)}>Cancel</Button>
            <Button onClick={activateSupport} disabled={actionLoading} className="bg-green-600">
              {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Activate Support'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reactivation Dialog */}
      <Dialog open={showReactivationDialog} onOpenChange={setShowReactivationDialog}>
        <DialogContent className="bg-gray-800 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white">Support Reactivation</DialogTitle>
            <DialogDescription>Calculate and process reactivation fee</DialogDescription>
          </DialogHeader>
          
          {reactivationCalc && (
            <div className="space-y-4">
              <div className="bg-gray-900 p-4 rounded-lg">
                <h4 className="text-white font-semibold mb-3">Reactivation Calculation</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Support Lapsed Since</span>
                    <span className="text-white">{reactivationCalc.lapsed_since?.slice(0, 10)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Missed Years</span>
                    <span className="text-white">{reactivationCalc.missed_years}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Annual Support Fee</span>
                    <span className="text-white">RM {reactivationCalc.annual_support_fee?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Missed Support Value</span>
                    <span className="text-white">RM {reactivationCalc.missed_support_value?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Reactivation Penalty (25%)</span>
                    <span className="text-orange-400">RM {reactivationCalc.reactivation_penalty?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Current Year Fee</span>
                    <span className="text-white">RM {reactivationCalc.current_year_fee?.toLocaleString()}</span>
                  </div>
                  <div className="border-t border-gray-700 pt-2 flex justify-between">
                    <span className="text-white font-semibold">Total to Pay</span>
                    <span className="text-green-400 font-bold">RM {reactivationCalc.total_to_pay?.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-white">Payment Reference</Label>
                <Input 
                  value={paymentForm.reference}
                  onChange={e => setPaymentForm({...paymentForm, reference: e.target.value})}
                  className="bg-gray-900 border-gray-700 text-white"
                  placeholder="Invoice/Receipt number"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReactivationDialog(false)}>Cancel</Button>
            <Button onClick={processReactivation} disabled={actionLoading || !reactivationCalc} className="bg-orange-600">
              {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Process Reactivation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent className="bg-gray-800 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white">Record Source Code Transfer</DialogTitle>
            <DialogDescription>Record the delivery of source code package</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-white">Version</Label>
              <Input 
                value={transferForm.version}
                onChange={e => setTransferForm({...transferForm, version: e.target.value})}
                className="bg-gray-900 border-gray-700 text-white"
                placeholder="e.g., 1.0.0"
              />
            </div>
            <div>
              <Label className="text-white">Package Hash (SHA256)</Label>
              <Input 
                value={transferForm.package_hash}
                onChange={e => setTransferForm({...transferForm, package_hash: e.target.value})}
                className="bg-gray-900 border-gray-700 text-white font-mono text-sm"
                placeholder="SHA256 hash of the delivered package"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferDialog(false)}>Cancel</Button>
            <Button onClick={recordTransfer} disabled={actionLoading} className="bg-blue-600">
              {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Record Transfer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
